import { useActionState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { overlay } from 'overlay-kit';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { api, getErrorMessage } from '@/shared/api/api';
import type { User } from '@/shared/types/user';
import { Button, Input, Label } from '@/shared/ui';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Separator } from '@/shared/ui/separator';
import { Text } from '@/shared/ui/text';
import { cn } from '@/shared/utils/cn';
import { useRememberEmailStore } from '@/store/remember-email';

const signinSchema = z.object({
  email: z.string().email({ message: '이메일 주소를 입력하세요.' }),
  password: z.string().min(8, { message: '8자 이상 입력하세요.' }),
});

export function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { rememberEmail, rememberedEmail, setRemember, saveEmail, clearEmail } =
    useRememberEmailStore();

  const initialState = {
    email: rememberedEmail || '',
    password: '',
    errors: {
      email: '',
      password: '',
    },
  };

  const { mutate: signin, isPending: loading } = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.post<User>('auth/signin', {
        email,
        password,
      }),
    onSuccess: (data) => {
      if (rememberEmail) {
        saveEmail(data.email);
      } else {
        clearEmail();
      }

      const from = searchParams.get('from');
      navigate(from || '/cmo-records/1');
    },
    onError: async (error: Error) => {
      const errorMessage = await getErrorMessage(error);
      await overlay.openAsync(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent showCloseButton>
            <DialogHeader>
              <DialogTitle>로그인 안내</DialogTitle>
              <DialogDescription>{errorMessage}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="primary">확인</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));
    },
  });

  const fn = async (
    prevState: typeof initialState,
    formData: FormData
  ): Promise<typeof initialState> => {
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const validatedFields = signinSchema.safeParse({ email, password });
    if (!validatedFields.success) {
      return {
        ...prevState,
        email,
        errors: {
          email: validatedFields.error.flatten().fieldErrors.email?.[0] || '',
          password: validatedFields.error.flatten().fieldErrors.password?.[0] || '',
        },
      };
    }

    signin(validatedFields.data);

    return {
      ...prevState,
      email,
      password,
      errors: {
        email: '',
        password: '',
      },
    };
  };

  const [state, submitForm, isPending] = useActionState(fn, initialState);

  return (
    <>
      <Helmet>
        <title>로그인 | 동구바이오제약</title>
      </Helmet>
      <div className="w-full max-w-[342px] space-y-7">
        <div className="flex flex-col gap-2 justify-center items-center">
          <Text
            tag="h1"
            typography="typo-title-l-bold"
          >
            동구바이오제약
          </Text>
        </div>
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col space-y-2">
            <form
              action={submitForm}
              id="signin-form"
            >
              <fieldset className="flex flex-col space-y-2">
                <legend className="sr-only">로그인</legend>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    autoComplete="email"
                    className={cn(state.errors.email && 'border-destructive')}
                    defaultValue={rememberedEmail}
                    id="email"
                    name="email"
                    placeholder="이메일 주소를 입력하세요."
                    required
                    size="md"
                    type="email"
                  />
                </div>

                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    autoComplete="current-password"
                    className={cn(state.errors.password && 'border-warning')}
                    id="password"
                    name="password"
                    placeholder="비밀번호를 입력하세요."
                    required
                    size="md"
                    type="password"
                  />
                  {state.errors.password && (
                    <Text
                      className="text-warning"
                      tag="p"
                      typography="typo-text-l"
                    >
                      {state.errors.password}
                    </Text>
                  )}
                </div>
              </fieldset>
            </form>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={rememberEmail}
                id="remember-email"
                onCheckedChange={(checked) => setRemember(checked === true)}
              />
              <Label
                className="text-sm font-normal cursor-pointer"
                htmlFor="remember-email"
              >
                아이디 기억하기
              </Label>
            </div>
          </div>

          <Button
            className="bg-gradient-to-r from-[#2778BE] to-[#8BCC6F]"
            flex
            form="signin-form"
            pending={isPending || loading}
            size="lg"
            type="submit"
          >
            로그인
          </Button>

          <div className="flex items-center gap-2 w-full">
            <Separator className="flex-1" />
            <Text
              className="text-muted-foreground text-center"
              tag="p"
              typography="typo-text-xl"
            >
              또는
            </Text>
            <Separator className="flex-1" />
          </div>

          <div className="flex flex-col gap-2">
            <Link to="/signup">
              <Text
                className="hover:underline cursor-pointer text-center"
                tag="p"
                typography="typo-text-xl"
              >
                회원가입
              </Text>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
